<template>
  <div class="gm-panel player-control-panel gm-scroll">
    <h3 class="gm-title panel-title">Player Control</h3>

    <!-- Spawn Player Section -->
    <div class="control-section">
      <h4 class="gm-section-title">Spawn Player/NPC</h4>
      <div class="gm-field">
        <label class="gm-label">Name</label>
        <input
          v-model="newPlayerName"
          type="text"
          class="gm-input"
          placeholder="Player name"
        />
      </div>
      <div class="gm-field">
        <label class="gm-label">Action Points</label>
        <input
          v-model.number="newPlayerActionPoints"
          type="number"
          class="gm-input"
          min="1"
          max="100"
        />
      </div>
      <div class="gm-field">
        <label class="gm-label">Character Image URL (optional)</label>
        <input
          v-model="newPlayerImage"
          type="text"
          class="gm-input"
          placeholder="https://example.com/character.png"
        />
      </div>
      <div class="spawner-info">
        <p class="info-text">
          Players spawn randomly on Player Spawners ({{ playerSpawnerCount }})
        </p>
        <p class="info-text">
          NPCs spawn randomly on NPC Spawners ({{ npcSpawnerCount }})
        </p>
      </div>
      <div class="button-group">
        <button
          class="gm-btn gm-btn--primary"
          @click="handleSpawnPlayer"
          :disabled="playerSpawnerCount === 0"
        >
          Spawn Player
        </button>
        <button
          class="gm-btn"
          @click="handleSpawnNPC"
          :disabled="npcSpawnerCount === 0"
        >
          Spawn NPC
        </button>
      </div>
    </div>

    <!-- Players List -->
    <div class="control-section">
      <h4 class="gm-section-title">Spawned Players ({{ players.length }})</h4>
      <div v-if="players.length === 0" class="gm-empty">
        <p class="gm-empty__hint">No players spawned yet</p>
      </div>
      <div v-else class="players-list">
        <div
          v-for="player in players"
          :key="player.id"
          class="player-item"
          :class="{ selected: selectedPlayer && selectedPlayer.id === player.id }"
          @click="handleSelectPlayer(player.id)"
        >
          <div class="player-info">
            <div class="player-name">{{ player.name }}</div>
            <div class="player-position">
              Position: ({{ player.x }}, {{ player.y }}, L{{ player.layer }})
            </div>
            <div class="player-ap">
              AP: {{ player.currentActionPoints }} / {{ player.maxActionPoints }}
            </div>
          </div>
          <div class="player-actions">
            <button
              class="gm-btn gm-btn--sm"
              @click.stop="handleRefillActionPoints(player.id)"
              title="Refill Action Points"
            >
              Refill
            </button>
            <button
              class="gm-btn gm-btn--sm gm-btn--danger"
              @click.stop="handleRemovePlayer(player.id)"
              title="Remove Player"
            >
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Selected Player Details -->
    <div v-if="selectedPlayer" class="control-section">
      <h4 class="gm-section-title">Selected Player</h4>
      <div class="player-details">
        <div class="detail-row">
          <span class="detail-label">Name:</span>
          <span class="detail-value">{{ selectedPlayer.name }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Position:</span>
          <span class="detail-value">
            ({{ selectedPlayer.x }}, {{ selectedPlayer.y }}, Layer {{ selectedPlayer.layer }})
          </span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Action Points:</span>
          <span class="detail-value">
            {{ selectedPlayer.currentActionPoints }} / {{ selectedPlayer.maxActionPoints }}
          </span>
        </div>
        <div class="action-buttons">
          <button class="gm-btn" @click="handleRefillActionPoints(selectedPlayer.id)">
            Refill AP
          </button>
          <button class="gm-btn gm-btn--danger" @click="handleRemovePlayer(selectedPlayer.id)">
            Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Grid Controls -->
    <div class="control-section">
      <h4 class="gm-section-title">Display Options</h4>
      <div class="toggle-group">
        <label class="toggle-label">
          <input
            type="checkbox"
            :checked="gridVisible"
            @change="toggleGridVisibility"
          />
          <span>Show Grid</span>
        </label>
        <label class="toggle-label">
          <input
            type="checkbox"
            :checked="backgroundVisible"
            @change="toggleBackgroundVisibility"
          />
          <span>Show Background</span>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'PlayerControlPanel',
  data() {
    return {
      newPlayerName: '',
      newPlayerActionPoints: 10,
      newPlayerImage: ''
    };
  },
  computed: {
    ...mapGetters('tabletopPlayer', [
      'currentMap',
      'players',
      'selectedPlayer',
      'gridVisible',
      'backgroundVisible',
      'gridConfig',
      'currentLayer'
    ]),
    playerSpawnerCells() {
      if (!this.currentMap || !this.currentMap.cells) return [];
      return this.currentMap.cells.filter(cell =>
        cell.spawner &&
        cell.spawner.type === 'player' &&
        cell.layer === this.currentLayer
      );
    },
    npcSpawnerCells() {
      if (!this.currentMap || !this.currentMap.cells) return [];
      return this.currentMap.cells.filter(cell =>
        cell.spawner &&
        cell.spawner.type === 'npc' &&
        cell.layer === this.currentLayer
      );
    },
    playerSpawnerCount() {
      return this.playerSpawnerCells.length;
    },
    npcSpawnerCount() {
      return this.npcSpawnerCells.length;
    }
  },
  methods: {
    ...mapActions('tabletopPlayer', [
      'spawnPlayer',
      'removePlayer',
      'selectPlayer',
      'refillActionPoints',
      'toggleGridVisibility',
      'toggleBackgroundVisibility'
    ]),

    getRandomSpawner(spawnerCells) {
      if (!spawnerCells || spawnerCells.length === 0) return null;
      const randomIndex = Math.floor(Math.random() * spawnerCells.length);
      return spawnerCells[randomIndex];
    },

    handleSpawnPlayer() {
      if (!this.newPlayerName.trim()) {
        alert('Please enter a player name');
        return;
      }

      if (!this.currentMap) {
        alert('No map loaded');
        return;
      }

      if (this.playerSpawnerCount === 0) {
        alert('No player spawners found on current layer. Please add player spawners in the Map Editor.');
        return;
      }

      // Get random player spawner
      const spawner = this.getRandomSpawner(this.playerSpawnerCells);
      if (!spawner) {
        alert('Failed to find player spawner');
        return;
      }

      this.spawnPlayer({
        x: spawner.x,
        y: spawner.y,
        layer: spawner.layer,
        name: this.newPlayerName.trim(),
        image: this.newPlayerImage.trim() || null,
        actionPoints: this.newPlayerActionPoints,
        type: 'player',
        skills: [
          {
            id: 1,
            name: 'Fireball',
            description: 'Launch a fireball at enemies',
            icon: null,
            min_range: 1,
            max_range: 8,
            spray_config: {
              spray: [[0, 1, 0], [1, 'x', 1], [1, 1, 1]],
              passthrough: false,
              shadow: true,
              multidimensional: false,
              rotatable: true
            }
          },
          {
            id: 2,
            name: 'Heal',
            description: 'Heal allies in range',
            icon: null,
            min_range: 0,
            max_range: 5,
            spray_config: {
              spray: [['x']],
              passthrough: true,
              shadow: false,
              multidimensional: false,
              rotatable: false
            }
          }
        ]
      });

      // Reset form
      this.newPlayerName = '';
      this.newPlayerActionPoints = 10;
      this.newPlayerImage = '';
    },

    handleSpawnNPC() {
      if (!this.newPlayerName.trim()) {
        alert('Please enter an NPC name');
        return;
      }

      if (!this.currentMap) {
        alert('No map loaded');
        return;
      }

      if (this.npcSpawnerCount === 0) {
        alert('No NPC spawners found on current layer. Please add NPC spawners in the Map Editor.');
        return;
      }

      // Get random NPC spawner
      const spawner = this.getRandomSpawner(this.npcSpawnerCells);
      if (!spawner) {
        alert('Failed to find NPC spawner');
        return;
      }

      this.spawnPlayer({
        x: spawner.x,
        y: spawner.y,
        layer: spawner.layer,
        name: this.newPlayerName.trim(),
        image: this.newPlayerImage.trim() || null,
        actionPoints: this.newPlayerActionPoints,
        type: 'npc',
        skills: [
          {
            id: 3,
            name: 'Slash',
            description: 'Melee attack',
            icon: null,
            min_range: 1,
            max_range: 1,
            spray_config: {
              spray: [['x']],
              passthrough: false,
              shadow: false,
              multidimensional: false,
              rotatable: false
            }
          }
        ]
      });

      // Reset form
      this.newPlayerName = '';
      this.newPlayerActionPoints = 10;
      this.newPlayerImage = '';
    },

    handleSelectPlayer(playerId) {
      this.selectPlayer(playerId);
    },

    handleRefillActionPoints(playerId) {
      this.refillActionPoints(playerId);
    },

    handleRemovePlayer(playerId) {
      if (confirm('Are you sure you want to remove this player?')) {
        this.removePlayer(playerId);
      }
    }
  }
};
</script>

<style scoped>
.player-control-panel {
  max-height: 37.5rem;
  padding-bottom: var(--gm-dock-clearance);
}

.panel-title {
  margin: 0 0 var(--gm-gap);
}

.control-section {
  background: var(--gm-surface-2);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius);
  padding: var(--gm-gap);
  margin-bottom: var(--gm-gap-lg);
}

.gm-field {
  margin-bottom: var(--gm-gap-sm);
}

.spawner-info {
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  padding: var(--gm-gap-sm);
  margin-bottom: var(--gm-gap-sm);
}

.info-text {
  font-size: 0.75rem;
  color: var(--gm-text-muted);
  margin: 0.25rem 0;
}

.button-group {
  display: flex;
  gap: var(--gm-gap-sm);
  margin-top: var(--gm-gap-sm);
}

.button-group .gm-btn {
  flex: 1;
}

.players-list {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

.player-item {
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  padding: var(--gm-gap-sm);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.player-item:hover {
  background: var(--gm-accent-soft);
  border-color: var(--gm-border);
}

.player-item.selected {
  background: rgba(255, 215, 0, 0.12);
  border-color: var(--gm-border-hi);
}

.player-info {
  flex: 1;
}

.player-name {
  font-size: 0.85rem;
  font-weight: bold;
  color: var(--gm-text);
  margin-bottom: 0.25rem;
}

.player-position,
.player-ap {
  font-size: 0.7rem;
  color: var(--gm-text-muted);
  margin-bottom: 0.1rem;
}

.player-actions {
  display: flex;
  gap: var(--gm-gap-xs);
}

.player-details {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 0.35rem 0;
  border-bottom: 1px solid var(--gm-border-dim);
}

.detail-label {
  font-size: 0.7rem;
  color: var(--gm-text-muted);
}

.detail-value {
  font-size: 0.7rem;
  color: var(--gm-text);
  font-weight: 600;
}

.action-buttons {
  display: flex;
  gap: var(--gm-gap-sm);
  margin-top: var(--gm-gap-sm);
}

.action-buttons .gm-btn {
  flex: 1;
}

.toggle-group {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  cursor: pointer;
  font-size: 0.8rem;
  color: var(--gm-text);
}

.toggle-label input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
  accent-color: var(--gm-accent);
}
</style>
